import { useEffect, useRef, useState } from "react";
import {
  LiaHornbill,
  LiaSearchPlusSolid,
  LiaSearchSolid,
} from "react-icons/lia";
import { MdClose, MdEdit, MdPrint } from "react-icons/md";
import { packingEntryAPI } from "../../utlis/apiService";
import { notify } from "../../common/Toast";
import Loader from "../../common/Loader";
import Table from "../../common/Table";
import Pagination from "../../common/Pagination";
import ConfirmDeleteModal from "../../common/DeleteModel";
import ErrorModal from "../../common/CustomModel";
import { HiOutlinePlus } from "react-icons/hi";
import PackingForm from "./PackingForm";
import Button from "../../common/Button";
import { IoCloseSharp } from "react-icons/io5";
import { IoIosSave } from "react-icons/io";
import { LayoutGrid } from "lucide-react";
import { RiHistoryLine } from "react-icons/ri";

const Packing = () => {
  const today = new Date().toISOString()?.split("T")[0];
  const [searchQuery, setSearchQuery] = useState("");
  const [isModelOpen, setIsModelOpen] = useState(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [errorSubMessage, setErrorSubMessage] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [selectedPackingData, setSelectedPackingData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isPackingData, setisPackingData] = useState([]);
  const [isAgingDoneData, setIsAgingDoneData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [formData, setFormData] = useState({
    bundle_No: null,
    packing_Date: today,
    order_No: "",
    batch_Id: "",
    die_Id: "",
    casting_No: "",
    dieNumber: "",
    cut_Length: "",
    wt_12_FT: "",
    wt_Cut_Length: "",
    pcs: "",
    total_Weight: "",
    series_Name: "",
    remark: "",
    // bundle_Pcs: "",
    // bundle_Weight: "",
    unit_Conv_Id: "",
    final_CutLength_weight: "",
  });
  const pageSize = 10;
  const [isActiveTab, setIsActiveTab] = useState("aging-done");
  const [selectedAgingNo, setSelectedAgingNo] = useState([]);
  const [adding, setAdding] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [draft, setDraft] = useState({ pcs: "", total_Weight: "" });
  const [editingRow, setEditingRow] = useState(null);
  const [selectedBundles, setSelectedBundles] = useState([]);
  const tableScrollRef = useRef(null);
  const isFetchingRef = useRef(false);
  const [packingHasMore, setPackingHasMore] = useState(true);
  const [agingHasMore, setAgingHasMore] = useState(true);
  const [packingPage, setPackingPage] = useState(1);
  const [agingPage, setAgingPage] = useState(1);

  const fetchPackingData = async (page) => {
    setLoading(true);
    try {
      const res = await packingEntryAPI.getAllPackingEntry(page, pageSize);
      setisPackingData((prev) => {
        return page === 1 ? res.data || [] : [...prev, ...(res.data || [])];
      });

      if (!res.data || res.data.length < pageSize) {
        setPackingHasMore(false);
      }
      isFetchingRef.current = false;
    } catch (error) {
      notify("Error fetching sales order master data", "error");
    }
    setLoading(false);
  };

  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredData(isPackingData);
    } else {
      const filtered = isPackingData.filter((d) =>
        d.status_Name.toLowerCase().includes(searchQuery.toLowerCase()),
      );
      setFilteredData(filtered);
    }
  }, [searchQuery, isPackingData]);

  const handleEdit = async (row) => {
    const bundle_No = row?.bundle_No;
    setEditingKey(bundle_No);
    setDraft({
      pcs: row.pcs ?? "",
      total_Weight: row.total_Weight ?? "",
    });
    try {
      const res = await packingEntryAPI.getById(bundle_No);
      const doc = res?.data ?? res;
      setEditingRow(doc);
    } catch (err) {
      notify("Failed to fetch packing entry details", "error");
    }
  };

  const cancelInlineEdit = () => {
    setEditingKey(null);
    setDraft({ pcs: "", total_Weight: "" });
    setEditingRow(null);
  };

  const buildPayload = () => {
    const src = editingRow ?? {};
    return {
      ...src,
      bundle_No: src.bundle_No,
      order_No: src.order_No,
      die_Id: src.die_Id,
      cut_Length: src.cut_Length,
      packing_Date: src.packing_Date,
      batch_Id: src.batch_Id,
      casting_No: src.casting_No,
      series_Name: src.series_Name,
      remark: src.remark,
      wt_12_FT: src.wT_12_FT ?? src.wt_12_FT ?? 0,
      wt_Cut_Length: src.wT_Cut_Length ?? src.wt_Cut_Length ?? 0,
      // bundle_Pcs: src.bundle_Pcs ?? null,
      // bundle_Weight: src.bundle_Weight ?? null,
      unit_Conv_Id: src.unit_Conv_Id,
      final_CutLength_weight: src.final_CutLength_weight,
      pcs: Number(draft.pcs || 0),
      total_Weight: parseFloat(draft.total_Weight || 0),
    };
  };

  const handleinlineSave = async (row) => {
    setIsSaving(true);
    try {
      const payload = buildPayload();
      await packingEntryAPI.saveOrUpdate(payload);
      notify("Saved successfully", "success");
      cancelInlineEdit();
      fetchPackingData();
      setIsModelOpen(false);
    } catch (err) {
      notify(err?.response?.data?.message || "Failed to save", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave = async (data, id) => {
    setIsSaving(true);
    try {
      const finalData = {
        bundle_No: data.bundle_No || "0",
        packing_Date: data.packing_Date,
        die_Id: data.die_Id,
        order_No: Number(data.order_No),
        dieNumber: data.dieNumber,
        batch_Id: data.batch_Id,
        casting_No: data.casting_No,
        cut_Length: Number(data.cut_Length),
        wt_12_FT: parseFloat(data.wt_12_FT || 0),
        wt_Cut_Length: parseFloat(data.wt_Cut_Length || 0),
        pcs: Number(data.pcs || 0),
        total_Weight: parseFloat(data.total_Weight || 0),
        series_Name: data.series_Name,
        remark: data.remark,
        // bundle_Pcs: data.bundle_Pcs,
        // bundle_Weight: data.bundle_Weight,
        unit_Conv_Id: data.unit_Conv_Id,
        final_CutLength_weight: data.final_CutLength_weight,
      };

      await packingEntryAPI.saveOrUpdate(finalData);
      notify(id ? "Updated successfully" : "Added successfully", "success");
      fetchPackingData();
      fetchAgingDoneData();
      setIsModelOpen(false);
      setFormData({
        bundle_No: "0",
        packing_Date: today,
        order_No: "",
        dieNumber: "",
        die_Id: "",
        batch_Id: "",
        casting_No: "",
        cut_Length: "",
        wt_12_FT: "",
        wt_Cut_Length: "",
        pcs: "",
        total_Weight: "",
        series_Name: "",
        remark: "",
        // bundle_Pcs: "",
        // bundle_Weight: "",
        unit_Conv_Id: "",
        final_CutLength_weight: "",
      });
      setIsEditing(false);
    } catch (error) {
      notify("Failed to submit form", "error");
      setErrorMessage("Submission failed");
      setErrorSubMessage("Please check your input or try again.");
      setErrorModalOpen(true);
    } finally {
      setIsSaving(false);
    }
  };

  const fetchAgingDoneData = async (page) => {
    setLoading(true);
    try {
      const res = await packingEntryAPI.getAllAgingDone(page, pageSize);
      setIsAgingDoneData((prev) => {
        return page === 1 ? res.data || [] : [...prev, ...(res.data || [])];
      });

      if (!res.data || res.data.length < pageSize) {
        setAgingHasMore(false);
      }
      isFetchingRef.current = false;
    } catch (error) {
      notify("Error fetching location data", "error");
    }
    setLoading(false);
  };

  const handleTabChange = (tab) => {
    setIsActiveTab(tab);
    if (tab === "aging-done") {
      setPackingPage(1);
      setIsAgingDoneData([]);
      setAgingHasMore(true);
    } else {
      setPackingPage(1);
      setisPackingData([]);
      setPackingHasMore(true);
    }
  };

  useEffect(() => {
    if (isActiveTab === "aging-done") fetchAgingDoneData(agingPage);
  }, [agingPage, isActiveTab]);

  useEffect(() => {
    if (isActiveTab === "packing") fetchPackingData(packingPage);
  }, [packingPage, isActiveTab]);

  useEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return;

    const onScroll = () => {
      if (loading || isFetchingRef.current) return;

      const { scrollTop, clientHeight, scrollHeight } = el;

      if (scrollHeight - scrollTop <= clientHeight + 100) {
        isFetchingRef.current = true;

        if (isActiveTab === "aging-done" && agingHasMore) {
          setAgingPage((p) => p + 1);
        }

        if (isActiveTab === "packing" && packingHasMore) {
          setPackingPage((p) => p + 1);
        }
      }
    };

    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, [isActiveTab, loading, packingHasMore, agingHasMore]);

  const columns = [
    {
      key: "select",
      label: null,
      render: (_v, row) => {
        const id = Number(row.seq_No);
        const isChecked = selectedAgingNo.includes(id);
        return (
          <input
            type="checkbox"
            checked={isChecked}
            onChange={(e) => {
              if (e.target.checked) {
                setSelectedAgingNo((prev) => [...prev, id]);
              } else {
                setSelectedAgingNo((prev) =>
                  prev.filter((item) => item !== id),
                );
              }
            }}
          />
        );
      },
    },
    { key: "order_No", label: "ORDER NO" },
    { key: "batch_Id", label: "BATCH NO" },
    { key: "dieNumber", label: "DIE NO" },
    { key: "cut_Length", label: "Cut Length" },
    { key: "quantity", label: "Pcs" },
    { key: "pending_Pcs", label: "PENDING PCS" },
    { key: "total_Weight", label: "Total weight" },
  ];

  const handleAddPacking = async () => {
    if (!selectedAgingNo) {
      notify("Please select one Aging-Done row.", "warning");
      return;
    }
    setAdding(true);
    try {
      const resp = await packingEntryAPI.AddToPacking(
        Number(selectedAgingNo[0]),
      );
      const x = Array.isArray(resp) ? resp[0] : resp;
      const init = {
        bundle_No: "0",
        packing_Date: today,
        order_No: x?.order_No ?? "",
        die_Id: x?.die_Id ?? null,
        dieNumber: x?.dieNumber ?? "",
        cut_Length: x?.cut_Length ?? "",
        batch_Id: x?.batch_Id ?? "",
        casting_No: x?.casting_No ?? "",
        total_Weight: x?.total_Weight ?? "",
        wt_12_FT: "",
        wt_Cut_Length: "",
        pcs: "",
        series_Name: x?.series_Name ?? "",
        remark: "",
        // bundle_Pcs: "",
        // bundle_Weight: "",
        unit_Conv_Id: "",
        final_CutLength_weight: "",
      };
      setSelectedPackingData(init);
      setIsActiveTab("packing");
      setIsModelOpen(true);
      setIsEditing(true);
      setSelectedAgingNo([]);
    } catch (e) {
      notify(
        e?.response?.data?.message || e?.message || "Failed to Add to Packing",
        "error",
      );
    } finally {
      setAdding(false);
    }
  };

  const handlePrint = async () => {
    if (selectedBundles.length === 0) {
      notify("Please select at least one bundle to print", "warning");
      return;
    }

    try {
      const payload = { bundleNos: selectedBundles };
      const res = await packingEntryAPI.packingBundles(payload);

      const blob = new Blob([res.data], { type: "application/pdf" });

      const pdfUrl = URL.createObjectURL(blob);
      window.open(pdfUrl, "_blank");
    } catch (error) {
      notify("Failed to print selected bundles", "error");
    }
  };

  const renderCell = (
    row,
    field,
    { type = "text", step, align = "right", editingKey, draft, setDraft } = {},
  ) => {
    const editable = editingKey === row.bundle_No;
    const value = editable ? (draft[field] ?? "") : (row[field] ?? "");

    if (editable) {
      return (
        <input
          type={type}
          step={step}
          value={value}
          onChange={(e) => setDraft((d) => ({ ...d, [field]: e.target.value }))}
          className={`w-full bg-white border border-blue-400 rounded px-1.5 py-1 outline-none focus:ring-1 focus:ring-blue-500 text-[12px] shadow-sm text-${align}`}
        />
      );
    }

    return (
      <span className={`block px-2 py-1 font-medium text-${align}`}>
        {value || value === 0 ? value : "-"}
      </span>
    );
  };

  return (
    <>
      <div>
        <div className="bg-white p-5 rounded-2xl shadow-sm border border-slate-100 mb-6">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6 mb-1">
            <div className="space-y-1">
              <h2 className="text-2xl font-bold text-slate-800 tracking-tight flex items-center gap-2">
                Packing Plan View
              </h2>
              <p className="text-sm text-slate-500 font-medium">
                Manage and track customer sales orders efficiently
              </p>
            </div>

            <div className="inline-flex p-1 bg-slate-200/50 rounded-xl backdrop-blur-sm">
              <button
                onClick={() => handleTabChange("aging-done")}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  isActiveTab === "aging-done"
                    ? "bg-white text-primary shadow-sm"
                    : "text-slate-600 hover:text-slate-800"
                }`}
              >
                <LayoutGrid size={18} /> Aging Done
              </button>
              <button
                onClick={() => handleTabChange("packing")}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  isActiveTab === "packing"
                    ? "bg-white text-primary shadow-sm"
                    : "text-slate-600 hover:text-slate-800"
                }`}
              >
                <RiHistoryLine size={18} /> Packing Entry
              </button>
            </div>
          </div>

          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="relative w-full md:w-80 group">
              <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                <LiaSearchSolid className="h-5 w-5 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
              </div>
              <input
                type="text"
                className="w-full bg-slate-50 border border-slate-200 py-2.5 pl-11 pr-10 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all placeholder:text-slate-400 text-sm"
                placeholder={
                  isActiveTab === "aging-done"
                    ? "Search by order, customer..."
                    : "Search packing details..."
                }
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-red-500 transition-colors"
                >
                  <MdClose size={20} />
                </button>
              )}
            </div>

            <div className="w-full md:w-auto">
              {isActiveTab === "aging-done" ? (
                <Button
                  text={
                    adding
                      ? "Adding..."
                      : `Add Packing ${selectedAgingNo.length > 0 ? `(${selectedAgingNo.length})` : ""}`
                  }
                  className={`w-full md:w-auto flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl font-bold text-sm transition-all duration-300 ${
                    selectedAgingNo?.length > 0
                      ? "bg-orange text-white shadow-lg shadow-orange hover:bg-orange-600 hover:-translate-y-0.5 active:scale-95 cursor-pointer"
                      : "bg-slate-100 text-slate-400 cursor-not-allowed"
                  }`}
                  onClick={handleAddPacking}
                  icon={LiaHornbill}
                  disabled={adding || selectedAgingNo.length === 0}
                />
              ) : (
                <button
                  onClick={handlePrint}
                  disabled={selectedBundles.length === 0}
                  className={`w-full md:w-auto flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl font-bold text-sm transition-all duration-300 ${
                    selectedBundles.length > 0
                      ? "bg-orange-500 text-white shadow-lg shadow-orange-200 hover:bg-orange-600 hover:-translate-y-0.5 active:scale-95 cursor-pointer"
                      : "bg-slate-100 text-slate-400 cursor-not-allowed"
                  }`}
                >
                  <MdPrint size={18} /> Print Sticker ({selectedBundles.length})
                </button>
              )}
            </div>
          </div>
        </div>
        <div
          ref={tableScrollRef}
          className="relative overflow-auto border border-slate-200 rounded-xl shadow-inner bg-white"
          style={{ height: "calc(100vh - 320px)" }}
        >
          {isActiveTab === "aging-done" && (
            <div className="min-w-max">
              <Table
                columns={columns}
                data={isAgingDoneData}
                showActions={false}
                onRowClick={(row) => setSelectedAgingNo(Number(row.seq_No))}
              />
            </div>
          )}
          {isActiveTab === "packing" &&
            (loading ? (
              <Loader />
            ) : (
              <>
                <div className="relative border border-gray-200 rounded-lg shadow-sm overflow-hidden bg-white">
                  <div className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-280px)]">
                    <table className="w-full text-[13px] text-left border-separate border-spacing-0">
                      <thead className="bg-gray-50 sticky top-0 z-30 shadow-sm">
                        <tr className="text-[11px] font-semibold text-gray-600 text-center uppercase tracking-wider">
                          <th className="p-3 border-b border-gray-200">✓</th>
                          <th className="p-3 border-b border-gray-200">
                            BUNDLE NO
                          </th>
                          <th className="p-3 border-b border-gray-200">
                            DIE NO
                          </th>
                          <th className="p-3 border-b border-gray-200">
                            SERIES
                          </th>
                          <th className="p-3 border-b border-gray-200">
                            PROFILE
                          </th>
                          <th className="p-3 border-b border-gray-200">
                            FINAL CUT LENGHT
                          </th>
                          <th className="p-3 border-b border-gray-200">UNIT</th>
                          <th className="p-3 border-b border-gray-200">
                            WT/12 FT
                          </th>
                          <th className="p-3 border-b border-gray-200">
                            FINAL CUT LENGTH Wt
                          </th>
                          <th className="p-3 border-b border-gray-200">PCS</th>
                          <th className="p-3 border-b border-gray-200">
                            TOTAL Wt
                          </th>
                          <th className="p-3 border-b border-gray-200">
                            ACTIONS
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {isPackingData.map((row, idx) => {
                          const isEditingRow = editingKey === row.bundle_No;
                          return (
                            <tr
                              className="hover:bg-slate-50"
                              key={row.bundle_No ?? idx}
                            >
                              <td className="p-2 text-center">
                                <input
                                  type="checkbox"
                                  checked={selectedBundles.includes(
                                    row.bundle_No,
                                  )}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedBundles((prev) => [
                                        ...prev,
                                        row.bundle_No,
                                      ]);
                                    } else {
                                      setSelectedBundles((prev) =>
                                        prev.filter(
                                          (id) => id !== row.bundle_No,
                                        ),
                                      );
                                    }
                                  }}
                                />
                              </td>

                              <td className="p-2 text-center font-bold text-slate-800">
                                {row.bundle_No}
                              </td>
                              <td className="p-2 text-center font-bold text-slate-800">
                                {row.dieNumber}
                              </td>

                              <td className="p-2 text-center font-bold text-slate-800">
                                {row.series_Name}
                              </td>

                              <td className="p-2 text-center font-bold text-slate-800">
                                {row.profile_Name}
                              </td>
                              <td className="p-2 text-center font-bold text-slate-800">
                                {row.wT_Cut_Length}
                              </td>
                              <td className="p-2 text-center font-bold text-slate-800">
                                {row.unit_Conv_Name}
                              </td>
                              <td className="p-2 text-center font-bold text-slate-800">
                                {row.wT_12_FT}
                              </td>
                              <td className="p-2 text-center font-bold text-slate-800">
                                {row.final_CutLength_Weight}
                              </td>

                              <td className="p-2 text-center">
                                {renderCell(row, "pcs", {
                                  align: "right",
                                  type: "number",
                                  step: "1",
                                  editingKey,
                                  draft,
                                  setDraft,
                                })}
                              </td>

                              <td className="p-2 text-center">
                                {renderCell(row, "total_Weight", {
                                  align: "right",
                                  type: "number",
                                  step: "0.001",
                                  editingKey,
                                  draft,
                                  setDraft,
                                })}
                              </td>

                              <td className="border p-1">
                                {!isEditingRow ? (
                                  <div className="inline-flex gap-2">
                                    <button
                                      type="button"
                                      className="p-1.5 bg-blue-50 text-blue-600 rounded hover:bg-blue-100"
                                      onClick={() => handleEdit(row)}
                                      disabled={
                                        !!editingKey &&
                                        editingKey !== row.bundle_No
                                      }
                                    >
                                      <MdEdit size={16} />
                                    </button>
                                  </div>
                                ) : (
                                  <div className="inline-flex gap-2">
                                    <Button
                                      type="button"
                                      className="p-1.5 bg-green-100 text-green-700 rounded hover:bg-green-200"
                                      onClick={() => handleinlineSave()}
                                      icon={IoIosSave}
                                      loading={isSaving}
                                      loadingText=""
                                    />
                                    <button
                                      type="button"
                                      className="p-1.5 bg-red-100 text-red-700 rounded hover:bg-red-200"
                                      onClick={cancelInlineEdit}
                                      disabled={isSaving}
                                    >
                                      <IoCloseSharp size={16} />
                                    </button>
                                  </div>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            ))}
        </div>
      </div>
      {isModelOpen && formData && (
        <PackingForm
          initialData={selectedPackingData}
          onSave={handleSave}
          onCancel={() => setIsModelOpen(false)}
          isEditing={isEditing}
          filteredData={filteredData}
          formData={formData}
          setFormData={setFormData}
          isSaving={isSaving}
        />
      )}

      {errorModalOpen && (
        <ErrorModal
          message={errorMessage}
          subMessage={errorSubMessage}
          onClose={() => setErrorModalOpen(false)}
        />
      )}
    </>
  );
};

export default Packing;
